import { watch } from 'vue'
import type { Router } from 'vue-router'
import { isAxiosError } from 'axios'
import api from '@/lib/api'
import { current_user } from '@/store/auth'
import type { RecorderInitOptions, SessionRecorderApi } from './types'
import { trackAction, trackError, setRecorderMetadados } from './track'

export { trackAction, trackError, setRecorderMetadados } from './track'

const enabled = import.meta.env.VITE_SCREEN_RECORDER_ENABLED === 'true'

let initOptions: Omit<RecorderInitOptions, 'onReady' | 'onError'> | null = null

function recorder(): SessionRecorderApi | undefined {
    return window.SessionRecorder
}

function syncUserContext(): void {
    const user = current_user.value
    if (!user) {
        return
    }
    recorder()?.setUsuario({ id: user.id, nome: user.nome })
    setRecorderMetadados({
        matricula: user.matricula ?? '',
        tipo_usuario: String(user.tipo ?? ''),
    })
}

function syncLojaFromRoute(router: Router): void {
    const loja = router.currentRoute.value.query.loja
    if (loja) {
        setRecorderMetadados({ loja_id: String(loja) })
    }
}

function loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve()
            return
        }
        const script = document.createElement('script')
        script.src = src
        script.onload = () => resolve()
        script.onerror = () => reject(new Error(`Não foi possível carregar ${src}`))
        document.head.appendChild(script)
    })
}

function startRecording(): void {
    const SR = recorder()
    if (!SR || !initOptions || SR.isActive()) {
        return
    }

    SR.init({
        ...initOptions,
        onReady: () => {
            syncUserContext()
        },
        onError: (error) => console.warn('[screen-recorder]', error),
    })
}

function registerAxiosInterceptor(recorderHost: string): void {
    api.interceptors.response.use(
        (response) => response,
        (error) => {
            if (!isAxiosError(error) || !error.response) {
                return Promise.reject(error)
            }

            const status = error.response.status
            if (status < 400) {
                return Promise.reject(error)
            }

            const url = `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}`
            if (url.includes('/ingest/') || url.includes(recorderHost)) {
                return Promise.reject(error)
            }

            const data = error.response.data
            const message =
                typeof data === 'object' && data !== null && 'error' in data
                    ? String((data as { error: unknown }).error)
                    : error.message

            trackError({ message, status, url })
            return Promise.reject(error)
        },
    )
}

export async function setupScreenRecorder(router: Router): Promise<void> {
    if (!enabled) {
        return
    }

    const endpoint = import.meta.env.VITE_SCREEN_RECORDER_ENDPOINT?.replace(/\/$/, '')
    const projectKey = import.meta.env.VITE_SCREEN_RECORDER_KEY
    if (!endpoint || !projectKey) {
        return
    }

    try {
        await loadScript(`${endpoint}/recorder.js`)
    } catch (error) {
        console.warn('[screen-recorder]', error)
        return
    }

    initOptions = {
        apiUrl: endpoint,
        projectKey,
        trackErrors: true,
        metadados: { ambiente: import.meta.env.MODE },
    }

    registerAxiosInterceptor(new URL(endpoint).host)
    startRecording()
    recorder()?.bindVueRouter(router)

    watch(current_user, (user, prev) => {
        if (user) {
            startRecording()
            syncUserContext()
            return
        }

        if (prev) {
            trackAction('logout')
            void recorder()?.stop()
        }
    })

    router.afterEach(() => syncLojaFromRoute(router))
}
